// on native, expo-print takes HTML and produces a full PDF from it
// on web, expo-print ignores the passed HTML and just window.prints() the visible part of the DOM
// this file provides the equivalent functionality for web.

// react-to-print is a potential library for this usecase, however it doesn't support being passed raw html, only a ref to a react component.
// so instead this implementation largely takes react-to-print's internal iframing mechanism, very slightly modified to take raw html
// (see: https://github.com/MatthewHerbst/react-to-print/blob/master/src/utils/generatePrintWindow.ts)

import type { FilePrintOptions, FilePrintResult } from "expo-print";
import isNil from "lodash/isNil";

import { toMs } from "@/utils/date.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

const IframeCleanupTimeoutMs = toMs({ minutes: 1 });

// FilePrintResult fields unused by current sole consumer of this functionality
const EmptyPrintResult: FilePrintResult = { numberOfPages: 0, uri: "" };

const createHiddenIframe = (): HTMLIFrameElement => {
  const iframe = globalThis.document.createElement("iframe");
  // display:none is not used so that the html prints correctly
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  return iframe;
};

const printToFileAsync = async ({
  html,
}: FilePrintOptions = {}): Promise<FilePrintResult> => {
  if (!isNotNilNotEmpty(html)) return EmptyPrintResult;

  return new Promise<FilePrintResult>((resolve) => {
    const iframe = createHiddenIframe();
    let wasCleanedUp = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (wasCleanedUp) return;
      wasCleanedUp = true;
      if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
      iframe.contentWindow?.removeEventListener("afterprint", cleanup);
      iframe.remove();
    };

    iframe.onload = () => {
      const frameWindow = iframe.contentWindow;
      if (isNil(frameWindow)) {
        cleanup();
        resolve(EmptyPrintResult);
        return;
      }
      frameWindow.addEventListener("afterprint", cleanup);
      // safety net in case `afterprint` is never emitted
      fallbackTimer = setTimeout(cleanup, IframeCleanupTimeoutMs);

      frameWindow.focus();
      frameWindow.print();
      // resolve once the print dialog has been triggered, iframe cleanup continues async
      resolve(EmptyPrintResult);
    };

    // load the html as the iframe's own document, isolated from the host page's styles and DOM
    iframe.srcdoc = html;
    globalThis.document.body.appendChild(iframe);
  });
};

export { printToFileAsync };
